// AiUsageHost — the impure shell edge of the operator's AI-usage pages
// (`/admin/ai-usage…`). It owns the three things a pure view cannot: the
// URL (parse on load and on Back/Forward, push on every navigation), the
// clock (the period is anchored at "now" when the page opened), and the
// reads (fetched per route, stale answers dropped). Everything it holds is
// handed to the pure `AiUsagePage` as props.
//
// The server decides access: a 401/403 on any read shows the no-access
// state. Mounted instead of the app shell by `httpMain` for these paths.

export { AiUsageHost };

import { Remote, Result } from "@bilda/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    type AiUsageAccess,
    AiUsagePage,
    type AiUsageRead,
} from "../../components/AiUsage/AiUsagePage.js";
import { TooltipProvider } from "../../components/Tooltip/Tooltip.js";
import type { ExternalError } from "../externalError.js";
import { getAuthMe } from "../httpClient.js";
import { clipboardHttp } from "../httpPorts/clipboard.js";
import { aiUsageApi, aiUsageQueryString, csvHref } from "./api.js";
import { periodRange } from "./period.js";
import {
    type AiUsageRoute,
    parseAiUsageRoute,
    serializeAiUsageRoute,
} from "./route.js";

const DEFAULT_ROUTE: AiUsageRoute = {
    tab: "overview",
    period: "month",
    filters: {},
    sort: "time",
    dir: "desc",
    page: 0,
    by: "workspace",
};

const routeOfLocation = (): AiUsageRoute =>
    parseAiUsageRoute(window.location.pathname, window.location.search) ??
    DEFAULT_ROUTE;

const isDenied = (e: ExternalError): boolean =>
    e._tag === "External.Forbidden" || e._tag === "External.Unauthorized";

// One read, refetched whenever `key` changes (undefined = not needed now).
// A late answer to an older key is dropped.
const useRead = <T,>(
    key: string | undefined,
    load: (key: string) => Promise<Result.Result<T, ExternalError>>,
): AiUsageRead<T> => {
    const [state, setState] = useState<AiUsageRead<T>>(Remote.notAsked);
    const latest = useRef<string | undefined>(undefined);
    useEffect(() => {
        latest.current = key;
        if (key === undefined) return;
        setState((prev) => Remote.startLoad(prev));
        load(key).then((r) => {
            if (latest.current !== key) return;
            setState(
                Result.isOk(r)
                    ? Remote.loaded(r.value)
                    : Remote.failed(r.error),
            );
        });
    }, [key, load]);
    return state;
};

const failedWith = <T,>(r: AiUsageRead<T>): ExternalError | undefined =>
    r._tag === "Remote.Failed" ? r.error : undefined;

const AiUsageHost = () => {
    const [route, setRoute] = useState<AiUsageRoute>(routeOfLocation);
    // The period is anchored when the page opens (a reload moves it).
    const [nowMs] = useState(() => Date.now());
    const [attempt, setAttempt] = useState(0);
    const [account, setAccount] = useState<string | undefined>(undefined);

    useEffect(() => {
        const onPop = () => setRoute(routeOfLocation());
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    useEffect(() => {
        getAuthMe().then((r) => {
            if (Result.isOk(r) && r.value._tag === "authed") {
                const v = r.value.value;
                const email =
                    typeof v === "object" &&
                    v !== null &&
                    "email" in v &&
                    typeof v.email === "string"
                        ? v.email
                        : undefined;
                setAccount(email);
            }
        });
    }, []);

    const navigate = useCallback((next: AiUsageRoute) => {
        const url = serializeAiUsageRoute(next);
        if (`${window.location.pathname}${window.location.search}` !== url)
            window.history.pushState(null, "", url);
        setRoute(next);
    }, []);

    const range = periodRange(route.period, nowMs);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Each read's key holds only what changes its answer, plus the retry count.
    const keyOf = (r: AiUsageRoute): string =>
        `${aiUsageQueryString(r, range, tz)}&_attempt=${attempt}`;
    const listRoute: AiUsageRoute = { ...route, by: "workspace" };
    const summary = useRead(
        route.tab === "overview"
            ? keyOf({ ...route, sort: "time", dir: "desc", page: 0 })
            : undefined,
        aiUsageApi.summary,
    );
    const calls = useRead(
        route.tab === "calls" ? keyOf(listRoute) : undefined,
        aiUsageApi.calls,
    );
    const rates = useRead(
        route.tab === "rates"
            ? keyOf({ ...route, filters: {}, page: 0, by: "workspace" })
            : undefined,
        aiUsageApi.rates,
    );
    const loadCall = useCallback(
        (key: string) => aiUsageApi.call(key.split("#")[0] ?? key),
        [],
    );
    const detail = useRead(
        route.tab === "calls" && route.callId !== undefined
            ? `${route.callId}#${attempt}`
            : undefined,
        loadCall,
    );

    const errors: readonly (ExternalError | undefined)[] = [
        failedWith(summary),
        failedWith(calls),
        failedWith(rates),
        failedWith(detail),
    ];
    const access: AiUsageAccess = errors.some(
        (e) => e !== undefined && isDenied(e),
    )
        ? "denied"
        : "allowed";

    return (
        <TooltipProvider>
            <AiUsagePage
                route={route}
                range={range}
                access={access}
                accountLabel={account}
                summary={summary}
                calls={calls}
                detail={detail}
                rates={rates}
                csvHref={csvHref(aiUsageQueryString(listRoute, range, tz))}
                onNavigate={navigate}
                onRetry={() => setAttempt((n) => n + 1)}
                onCopy={(text) => {
                    clipboardHttp.copyText(text);
                }}
            />
        </TooltipProvider>
    );
};
